import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { TriggerSSICompletionEmail } from './functions/TriggerSSICompletionEmail';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { renderModelInput } from '../../../utils/renderModelInput';
import { checkReportLogicVersion } from '../../../utils/featureFlagUtils';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';
import { rerenderCoverPageIfStale } from '../../../utils/autoCoverPage';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { useAuth } from '../../../hooks/useAuth';
import { useApprovalTracking } from '../../../hooks/useApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import SSIOverrideBanner from '../../../components/SSIOverrideBanner';
import {
  getEffectiveSSIClinician,
  isSSILeadOverrideActive,
  isLeadClinician,
} from '../../../utils/clinicianPermissions';
import {
  emitParagraphApproval,
  emitParagraphDeleted,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';
import { useAnthropicComparison } from '../../../hooks/useAnthropicComparison';
import { CompareModeToolbar } from './components/CompareModeToolbar';
import { CompareClaudePanel } from './components/CompareClaudePanel';

// Strength paragraph codes — used to gate the "Delete" action to only
// strength paragraphs and to track which strengths have been deleted.
const STRENGTH_CODES = new Set([
  'pl_strength_1_summary',
  'pl_strength_2_summary',
  'pl_strength_3_summary',
]);

// Static criterion and domain section definitions for SSI review documents.
// Extracted at module level so they can be tested independently of the React
// component. Keep in sync with assessment_data_model.py DSM_symptom values.
export const SSI_LEGACY_CRITERION_SECTIONS = {
  "Criterion A1a: Often fails to pay close attention to details, or makes careless mistakes in schoolwork, work or during other activities": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_1" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_1" },
  },
  "Criterion A1b: Often has difficulty sustaining attention in tasks or play activities": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_2" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_2" },
  },
  "Criterion A1c: Often does not seem to listen when spoken to directly": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_3" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_3" },
  },
  "Criterion A1d: Often does not follow through on instructions and fails to finish schoolwork, chores, or duties in the workplace": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_4" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_4" },
  },
  "Criterion A1e: Often has difficulty organising tasks and activities": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_5" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_5" },
  },
  "Criterion A1f: Often avoids, dislikes, or is reluctant to engage in tasks that require sustained mental effort": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_6" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_6" },
  },
  "Criterion A1g: Often loses things necessary for tasks or activities": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_7" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_7" },
  },
  "Criterion A1h: Often easily distracted by extraneous stimuli": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_8" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_8" },
  },
  "Criterion A1i: Often forgetful in daily activities": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_9" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_9" },
  },
  "Criterion A2a: Often fidgets with or taps hands or feet or squirms in seat": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_10" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_10" },
  },
  "Criterion A2b: Often leaves seat in situations when remaining seated is expected": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_11" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_11" },
  },
  "Criterion A2c: Often feels restless": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_12" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_12" },
  },
  "Criterion A2d: Often unable to play or take part in leisure activities quietly": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_13" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_13" },
  },
  "Criterion A2e: Is often \"on the go\" acting as if \"driven by a motor\"": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_14" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_14" },
  },
  "Criterion A2f: Often talks excessively": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_15" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_15" },
  },
  "Criterion A2g: Often blurts out an answer before a question has been completed": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_16" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_16" },
  },
  "Criterion A2h: Often has difficulty awaiting their turn": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_17" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_17" },
  },
  "Criterion A2i: Often interrupts or intrudes on others": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_1_18" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_1_18" },
  },
};

export const SSI_DOMAIN_SECTIONS = {
  "Domain of Functional Impairment: Work / Education": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_2_1" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_2_1" },
  },
  "Domain of Functional Impairment: Social": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_2_2" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_2_2" },
  },
  "Domain of Functional Impairment: Psychological": {
    "1": { "title": "Adulthood", "code": "ssi_Adulthood_3_2_3" },
    "2": { "title": "Childhood", "code": "ssi_Childhood_3_2_3" },
  },
};

export const SSI_UNIFIED_CRITERION_SECTIONS = {
  "Criterion A1a: Often fails to pay close attention to details, or makes careless mistakes in schoolwork, work or during other activities": {
    "1": { "title": "Criterion A1a", "code": "ssi_unified_3_1_1" },
  },
  "Criterion A1b: Often has difficulty sustaining attention in tasks or play activities": {
    "1": { "title": "Criterion A1b", "code": "ssi_unified_3_1_2" },
  },
  "Criterion A1c: Often does not seem to listen when spoken to directly": {
    "1": { "title": "Criterion A1c", "code": "ssi_unified_3_1_3" },
  },
  "Criterion A1d: Often does not follow through on instructions and fails to finish schoolwork, chores, or duties in the workplace": {
    "1": { "title": "Criterion A1d", "code": "ssi_unified_3_1_4" },
  },
  "Criterion A1e: Often has difficulty organising tasks and activities": {
    "1": { "title": "Criterion A1e", "code": "ssi_unified_3_1_5" },
  },
  "Criterion A1f: Often avoids, dislikes, or is reluctant to engage in tasks that require sustained mental effort": {
    "1": { "title": "Criterion A1f", "code": "ssi_unified_3_1_6" },
  },
  "Criterion A1g: Often loses things necessary for tasks or activities": {
    "1": { "title": "Criterion A1g", "code": "ssi_unified_3_1_7" },
  },
  "Criterion A1h: Often easily distracted by extraneous stimuli": {
    "1": { "title": "Criterion A1h", "code": "ssi_unified_3_1_8" },
  },
  "Criterion A1i: Often forgetful in daily activities": {
    "1": { "title": "Criterion A1i", "code": "ssi_unified_3_1_9" },
  },
  "Criterion A2a: Often fidgets with or taps hands or feet or squirms in seat": {
    "1": { "title": "Criterion A2a", "code": "ssi_unified_3_1_10" },
  },
  "Criterion A2b: Often leaves seat in situations when remaining seated is expected": {
    "1": { "title": "Criterion A2b", "code": "ssi_unified_3_1_11" },
  },
  "Criterion A2c: Often feels restless": {
    "1": { "title": "Criterion A2c", "code": "ssi_unified_3_1_12" },
  },
  "Criterion A2d: Often unable to play or take part in leisure activities quietly": {
    "1": { "title": "Criterion A2d", "code": "ssi_unified_3_1_13" },
  },
  "Criterion A2e: Is often \"on the go\" acting as if \"driven by a motor\"": {
    "1": { "title": "Criterion A2e", "code": "ssi_unified_3_1_14" },
  },
  "Criterion A2f: Often talks excessively": {
    "1": { "title": "Criterion A2f", "code": "ssi_unified_3_1_15" },
  },
  "Criterion A2g: Often blurts out an answer before a question has been completed": {
    "1": { "title": "Criterion A2g", "code": "ssi_unified_3_1_16" },
  },
  "Criterion A2h: Often has difficulty awaiting their turn": {
    "1": { "title": "Criterion A2h", "code": "ssi_unified_3_1_17" },
  },
  "Criterion A2i: Often interrupts or intrudes on others": {
    "1": { "title": "Criterion A2i", "code": "ssi_unified_3_1_18" },
  },
};

export const SSI_UNIFIED_DOMAIN_SECTIONS = {
  "Domain of Functional Impairment: Work / Education": {
    "1": { "title": "Work / Education", "code": "ssi_unified_3_2_1" },
  },
  "Domain of Functional Impairment: Social": {
    "1": { "title": "Social", "code": "ssi_unified_3_2_2" },
  },
  "Domain of Functional Impairment: Psychological": {
    "1": { "title": "Psychological", "code": "ssi_unified_3_2_3" },
  },
};

// Strips the legacy "N. " number prefix from strength titles stored in
// Firestore (e.g. "1. Hyperfocus" -> "Hyperfocus"). Existing patients may
// have numbered titles; new patients will already be unnumbered.
const stripStrengthPrefix = (title) => {
  if (!title) return null;
  return title.includes('. ') ? title.split('. ')[1] : title;
};

export default function ReviewDocumentSSI() {

  const { patientId } = useParams();
  const { claims } = useAuth();

  const MODULE_NUMBER = 2
  const MODULE_PREFIX_CODE = "ssi"
  const MODULE_TITLE = "Semi-Structured Interview"

  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);

  // In-page permission check (closes the previous deep-link gap where anyone
  // who could load the patient could approve paragraphs). When a Lead SSI
  // override is active, the Lead is the effective reviewer.
  const effectiveSSI = useMemo(
    () => getEffectiveSSIClinician(patientDataModel),
    [patientDataModel]
  );
  const canEdit = Boolean(
    auth.currentUser?.uid &&
    effectiveSSI.clinicianId &&
    auth.currentUser.uid === effectiveSSI.clinicianId
  );
  const ssiOverrideActive = isSSILeadOverrideActive(patientDataModel);
  const ssiLeadOverride =
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
  const snapshotPath = ssiLeadOverride?.archiveId
    ? `/patient/${patientId}/ssi-snapshot/${ssiLeadOverride.archiveId}`
    : null;

  // State for feature flag
  const [reportLogicVersion, setReportLogicVersion] = useState(1);
  const [isLoadingFeatureFlag, setIsLoadingFeatureFlag] = useState(true);

  useEffect(() => {
    // Force fresh fetch
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // Check feature flag when component mounts
  useEffect(() => {
    const checkFeatureFlag = async () => {
      if (!patientId) return;
      
      try {
        setIsLoadingFeatureFlag(true);
        const version = await checkReportLogicVersion(patientId);
        setReportLogicVersion(version);
      } catch (error) {
        console.error('Error checking feature flag:', error);
        setReportLogicVersion(1); // Default to old logic
      } finally {
        setIsLoadingFeatureFlag(false);
      }
    };

    checkFeatureFlag();
  }, [patientId]);

  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  // Legacy document structure (version 1)
  const legacyDocStructure = useMemo(() => ({
    ...SSI_LEGACY_CRITERION_SECTIONS,
    ...SSI_DOMAIN_SECTIONS,
  }), []);

  // Unified document structure (version 2)
  const unifiedDocStructure = useMemo(() => ({
    ...SSI_UNIFIED_CRITERION_SECTIONS,
    ...SSI_UNIFIED_DOMAIN_SECTIONS,
    "ADHD Strengths": {
      "1": { "title": stripStrengthPrefix(REPORT_DATA_OUTPUTS['pl_strength_1']) || "Strength 1", "code": "pl_strength_1_summary" },
      "2": { "title": stripStrengthPrefix(REPORT_DATA_OUTPUTS['pl_strength_2']) || "Strength 2", "code": "pl_strength_2_summary" },
      "3": { "title": stripStrengthPrefix(REPORT_DATA_OUTPUTS['pl_strength_3']) || "Strength 3", "code": "pl_strength_3_summary" },
    },
  }), [REPORT_DATA_OUTPUTS]);

  // Choose the appropriate structure based on feature flag
  const docStructure = useMemo(() => {
    if (isLoadingFeatureFlag) {
      return legacyDocStructure; // Default to legacy while loading
    }
    return reportLogicVersion === 2 ? unifiedDocStructure : legacyDocStructure;
  }, [reportLogicVersion, isLoadingFeatureFlag, legacyDocStructure, unifiedDocStructure]);

  // Subtitle text shown beneath the main heading for specific sections.
  // Only the ADHD Strengths section currently uses this — the parenthetical
  // note clarifies that the section only applies when a positive diagnosis
  // is made, without cluttering the main heading.
  const SECTION_SUBTITLES = {
    "ADHD Strengths": "to be used if positive diagnosis is made",
  };

  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    checkedExamples: null,
    patientInputs: null,
    informantInputs: null
  });

  const [isEditing, setIsEditing] = useState(false);

  // Tracks strength codes the clinician has deleted during this session.
  // Fed into useApprovalTracking as excludedCodes so totalParagraphs and
  // progress update optimistically before the Firestore listener fires.
  const [deletedCodes, setDeletedCodes] = useState(new Set());

  const isStrengthParagraph = useCallback(
    (code) => STRENGTH_CODES.has(code),
    []
  );

  // Confirmation dialog state for the "Delete" action on strength paragraphs.
  // handleDeleteStrength opens the dialog; executeDeleteStrength runs the deletion.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteCode, setPendingDeleteCode] = useState(null);

  // How many strength paragraphs are still visible (not yet deleted).
  // Used to display a different confirmation message when deleting the last one.
  const remainingStrengthCount = useMemo(() => {
    return [...STRENGTH_CODES].filter(
      code => !deletedCodes.has(code) && REPORT_DATA_OUTPUTS[code] && REPORT_DATA_OUTPUTS[code] !== ''
    ).length;
  }, [deletedCodes, REPORT_DATA_OUTPUTS]);

  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useApprovalTracking({
    patientId,
    moduleNumber: MODULE_NUMBER,
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    excludedCodes: deletedCodes,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ── Anthropic comparison (dev/staging UAT only) ──────────────────────────
  // SSI backend section titles differ from docStructure display keys (which
  // are per-criterion headings like "Criterion A1a: ..."). The backend
  // processes all symptom paragraphs under "Symptoms", domain paragraphs
  // under "Domains of Functional Impairment", and the recommendation under
  // "Recommended Diagnosis". We must use these Firestore sectionTitle values.
  const ssiSections = useMemo(
    () => ['Symptoms', 'Domains of Functional Impairment', 'Recommended Diagnosis'],
    []
  );
  const {
    isAvailable: isCompareAvailable,
    isLoading: isCompareLoading,
    hasComparison,
    showClaude,
    sideBySide,
    claudeOutputs,
    progress: compareProgress,
    triggerComparison,
    setViewMode,
    toggleSideBySide,
    getDisplayedOutput,
  } = useAnthropicComparison({
    patientId,
    reportStage: MODULE_TITLE,
    reportSections: ssiSections,
    conditions: [],
    orgId: patientDataModel?.orgId,
    organisationName: claims?.organisation,
  });

  const handleParagraphClick = (event, code, content) => {
    // Reset edit states
    setIsEditing(false);

    // Position will be handled by shared hook

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ', // always having at least a non-breaking space
        clinicianInputs: state.modelInputsFirestore[code]["Clinician Notes"],
        clinicianDecisions: state.modelInputsFirestore[code]["Clinician Decisions"],
        checkedExamples: state.modelInputsFirestore[code]["Checklist Examples Ticked"],
        patientInputs: state.modelInputsFirestore[code]["Patient Prefill"],
        informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true
      }));
      setTimeout(() => {
        safeUpdatePosition(code);
      }, 0);
    } else {
      console.error('No matching input found for the code:', code);
    }
  };


  // Update the Section component to only render paragraphs with content
  const Section = ({ title, subtitle, paragraphs }) => {
    // First filter out paragraphs that don't have content in REPORT_DATA_OUTPUTS
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    // Only render the section if it has at least one available paragraph
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1 text-blue-500">{title}</h2>
        {subtitle && (
          <p className="text-sm text-gray-400 italic mb-4">{subtitle}</p>
        )}
        {!subtitle && <div className="mb-3" />}

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = getDisplayedOutput(code, REPORT_DATA_OUTPUTS[code]);
          const decodedContent = decodeHtmlEntities(content); // Decode for display        
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20' // Edit mode background
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code && !showClaude}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Add a ref for the paragraph content
  const editableContentRef = useRef(null);

  // Update handleStartEdit to focus the content
  const handleStartEdit = () => {
    if (state.selectedCode) {
      // Store the current content before starting edit
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    // Use setTimeout to ensure the contentEditable is enabled before focusing
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        // Create a range and selection to place cursor at the end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error('Only the clinician reviewing this Semi-Structured Interview can edit paragraphs.');
      return;
    }
    if (state.selectedCode) {
      // Get the current and previous values
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      // Only proceed if content actually changed
      if (newValue !== previousValue) {
        // Optimistically update UI first
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);


        // Create history entry
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Base path to this section. MODULE_NUMBER and timestampKey are numeric
        // segments; Firestore's SDK backtick-escapes digit-leading segments
        // automatically when it builds the update mask, so the dotted-string key
        // form is safe here (and matches handleParagraphApproval exactly).
        const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

        // Write ONLY the affected keys with a plain updateDoc rather than a
        // read-modify-write runTransaction. Transactions commit with a
        // document-version precondition; on slow 3G/4G the single patient doc is
        // being touched concurrently (in-flight approvals, auto report/cover
        // re-renders, lastReviewedAt bumps) so the transaction exhausts its
        // retries and throws "the stored version does not match the required
        // base version" — which silently discarded the clinician's edit. A
        // targeted updateDoc has no version precondition and Firestore merges
        // writes to distinct field paths server-side. See handleParagraphApproval
        // for the same reasoning applied to approvals.
        const updateData = {
          // Persist the edited text (targeted, replacing the whole-map rewrite)
          [`${sectionPath}.outputs.${spanCode}`]: newValue,
          // Un-approve this paragraph: editing invalidates a prior approval
          [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
          // Append an audit entry under this paragraph's edit history
          [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
          // Review metadata (mirrors the approval write)
          [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
          [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
          [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
        };

        // Perform Firestore update in the background
        const startedAt = performance.now();
        updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
          // Confirms the edit persisted AND the paragraph was un-approved
          // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph approval on edit:", error);
          // Roll back optimistic update on error and surface the failure
          addReviewedCode(state.selectedCode);
          toast.error(
            'Your edit did not save. Please try editing the paragraph again.',
            { toastId: 'paragraph-edit-save-error' }
          );
        });
        // Mark section as dirty since a change was saved
        setSectionDirty(true);
      } else {
        // If no changes, just exit edit mode
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);
      }
    }
  };

  // Update handleCancel to revert to pre-edit content
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      // Revert to the content before this edit session started
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      // Optimistically update UI
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      // Remove from reviewed codes optimistically
      removeReviewedCode(state.selectedCode);

      // Create history entry for the reset
      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true  // Flag to indicate this was a reset action
      };
      const timestampKey = Date.now().toString();

      // Same targeted-write approach as handleSaveEdit / handleParagraphApproval:
      // a plain updateDoc on specific field paths (no version precondition)
      // instead of a read-modify-write runTransaction, which contends on the
      // single patient doc and fails under slow 3G/4G (losing the reset).
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;
      const updateData = {
        // Restore the original generated text
        [`${sectionPath}.outputs.${spanCode}`]: newValue,
        // A reset also invalidates any prior approval
        [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
        // Record the reset in this paragraph's edit history
        [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Perform Firestore update
      const startedAt = performance.now();
      updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
        // Confirms the reset persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back optimistic updates on error and surface the failure
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        toast.error(
          'The paragraph reset did not save. Please try again.',
          { toastId: 'paragraph-reset-save-error' }
        );
      });

      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);
      // Mark section as dirty since content was reset
      setSectionDirty(true);
    }
  };

  // Helper function to check if content has been modified from original
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  const [showInitialWarning, setShowInitialWarning] = useState(true);
  // showWarning is provided by useApprovalTracking

  // State for initial content (on page load)
  const [initialContent, setInitialContent] = useState({});
  // Store initial content on page load, only for paragraphs with content
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS) {
      const initialValues = {};
      // Loop through docStructure to get all codes
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          // Only store content that exists
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // State for pre-edit content (before current edit session)
  const [preEditContent, setPreEditContent] = useState({});

  const documentContainerRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  const handleExitReview = async () => {
    setIsSaving(true);

    try {
      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh snapshot (deleted strength codes excluded, exactly as on-screen).
      const { isFullyApproved: approvedNow, outstandingCodes, manageReports } = await finalizeSectionReview({
        patientId,
        modulePrefix: MODULE_PREFIX_CODE,
        moduleNumber: MODULE_NUMBER,
        docStructure,
        excludedCodes: deletedCodes,
      });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when either:
      //   1. "Phantom approvals" exist — paragraphs shown green whose background
      //      write actually failed (rapid clicking can overload the per-document
      //      transaction queue), or
      //   2. the UI believed everything was approved but the snapshot found
      //      outstanding content (e.g. paragraphs generated after page load).
      // The affected paragraphs are reverted to needs-review and the patient
      // document is re-fetched so the page reflects reality.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await fetchPatientData(patientId);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      // Only render section if AUTHORITATIVELY approved AND changed this session.
      const shouldRenderSection = approvedNow && sectionDirty;

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: approvedNow && !manageReports?.ssiReportFirstApprovedAt,
      });

      // Only trigger document generation if fully approved AND this section was changed during this session
      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "Semi-Structured Interview",
          clinicianId: auth.currentUser.uid,
          patientId: patientId
        });
      }

      // MDT-information-available email. The server decides when it is ready
      // per org (see TriggerSSICompletionEmail.js): two-clinician orgs
      // (Phoenix, SLaM) send as soon as this SSI report is approved;
      // single-clinician orgs (TOC, WLC) wait until the CI report is approved
      // too, and the CI exit (RD_CI.js) fires the same trigger, so the email
      // goes out on whichever review is approved second. Gated on the
      // snapshot-derived approval so an unapproved exit does not spend a
      // callable invocation that the server would only skip. Deliberately
      // NOT gated on shouldRenderSection: a no-change re-exit of an approved
      // report must still be able to send if the CI was approved in between.
      if (approvedNow) {
        TriggerSSICompletionEmail(patientId, auth.currentUser.uid);
      }
      // Kick off a background check to re-render the Patient Report if it's stale.
      // Fire-and-forget to keep the UI responsive; helper skips if not needed.
      rerenderPatientReportIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: claims?.organisation
      }).catch((e) => console.error('rerenderPatientReportIfStale (SSI) failed:', e));

      // Also re-render the Cover Page if stale (flow-aware gating)
      rerenderCoverPageIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: claims?.organisation
      }).catch((e) => console.error('rerenderCoverPageIfStale (SSI) failed:', e));

      // Clear any cached data before navigating
      clearPatientData();

      // Navigate back to documents page
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
      // Reset dirty flag for next session
      setSectionDirty(false);
    }
  };

  const goToFirstUnreviewed = () => {
    // Only get paragraphs that are actually rendered on the page
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      // Calculate scroll position to center the paragraph
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      // Scroll to position
      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // After scroll completes, trigger click handler
      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        // Trigger the full paragraph click handler
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  useEffect(() => {
    // Apply the overflow: hidden style when the component mounts
    document.body.style.overflow = 'hidden';
    // Clean up the style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Add useEffect to fetch model inputs
  useEffect(() => {
    const fetchModelInputs = async () => {
      try {
        const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
        if (inputs) {
          setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
        } else {
          console.error('Inputs data is missing');
        }
      } catch (error) {
        console.error('Error fetching model inputs from Firestore:', error);
      }
    };

    if (patientDataModel) {
      fetchModelInputs();
    }
  }, [patientDataModel]);


  // Modify the overlay to explicitly allow scroll events
  useEffect(() => {
    // Store ref in a variable inside the effect
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      // Save original overflow setting
      const originalOverflow = currentContainer.style.overflow;

      // Ensure the document container is still scrollable during editing
      currentContainer.style.overflow = 'auto';

      // Create a wheel event handler that prevents propagation but allows default behavior
      const handleWheel = (e) => {
        e.stopPropagation();
        // But let the default scroll behavior happen
      };

      // Add the wheel event listener
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      // Clean up function uses the stored variable
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    // Ignore clicks if we're in edit mode
    if (isEditing) {
      return;
    }

    // Ignore clicks on paragraphs or the action panel
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    // Reset state
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false
    }));
  }, [isEditing]);

  // Add useEffect to set up and clean up click handler
  useEffect(() => {
    // Store ref in a variable inside the effect
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      // Use the stored variable in cleanup
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // Position updates handled by shared hook; no local updateActionPanelPosition

  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error('Only the clinician reviewing this Semi-Structured Interview can approve paragraphs.');
      return;
    }
    try {
      const docRef = doc(db, 'patients', patientId);

      // Base path to this section's approval bookkeeping. MODULE_NUMBER is a
      // numeric section id (e.g. "2"). Firestore's SDK backtick-escapes such
      // digit-leading segments automatically when it builds the update mask,
      // so the dotted-string key form is safe here.
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

      // Write ONLY this paragraph's key rather than reading the whole document
      // and rewriting the entire approvedParagraphs map inside a
      // read-modify-write transaction.
      //
      // Why: runTransaction uses optimistic concurrency — every commit carries
      // a document-version precondition. When a clinician approves several
      // paragraphs in quick succession (especially on slow 3G/4G links), the
      // queued transactions all contend on the single patient document and most
      // fail with "the stored version does not match the required base version"
      // (failed-precondition). A plain updateDoc on a specific field path has no
      // version precondition, and Firestore merges concurrent writes to
      // distinct field paths server-side, so per-paragraph approvals no longer
      // fight over the document. (Paragraph codes are dot-free identifiers, and
      // the four field paths below are siblings — none is an ancestor of
      // another — so there is no conflicting-path rejection.)
      const updateData = {
        [`${sectionPath}.approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Time the write so we can see (in the console) how quickly approvals
      // actually land in Firestore — updateDoc resolves on SERVER ack, so this
      // is the true round-trip, useful when stress-testing on 3G/Slow 4G. The
      // log is deliberately code-only (no patient content) to keep it PII-free.
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      // Mark section as dirty due to approval change
      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // Patient Reports finalising with missing content.
      throw error;
    }
  };

  // Approved paragraphs are now loaded by useApprovalTracking

  // Opens the confirmation dialog for deleting a strength paragraph.
  // The actual deletion is deferred to executeDeleteStrength so the
  // clinician can cancel without side-effects.
  const handleDeleteStrength = () => {
    if (!state.selectedCode || !isStrengthParagraph(state.selectedCode)) return;
    setPendingDeleteCode(state.selectedCode);
    setShowDeleteConfirm(true);
  };

  // Performs the actual strength deletion after the clinician confirms.
  // Clears the output to "" (leveraging existing empty-string filters),
  // Removes the approval record and logs a deletion entry in editHistory.
  const executeDeleteStrength = () => {
    const code = pendingDeleteCode;
    if (!code) return;

    const titleCode = code.replace('_summary', '');
    const previousValue = REPORT_DATA_OUTPUTS[code];
    const previousTitleValue = REPORT_DATA_OUTPUTS[titleCode];
    const wasReviewed = reviewedCodes.has(code);
    const timestampKey = Date.now().toString();

    // --- Optimistic UI ---
    REPORT_DATA_OUTPUTS[code] = "";
    REPORT_DATA_OUTPUTS[titleCode] = "";
    setDeletedCodes(prev => new Set([...prev, code]));
    removeReviewedCode(code);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      isSidePanelOpen: false,
      clinicianInputs: null,
      clinicianDecisions: null,
      checkedExamples: null,
      patientInputs: null,
      informantInputs: null,
    }));
    setShowDeleteConfirm(false);
    setPendingDeleteCode(null);

    // --- Firestore write (targeted per-key updateDoc) ---
    // Mirrors handleParagraphApproval / handleSaveEdit: a plain updateDoc on
    // specific field paths, NOT a read-modify-write runTransaction, so it does
    // not contend on the single patient doc under slow 3G/4G (which is how the
    // transactional writes failed and lost the clinician's action).
    //
    // Note the deliberate asymmetry: the two output keys are cleared to an
    // empty string (not deleteField()) so the existing empty-string filters
    // keep the paragraph hidden, while only the approval record is removed.
    const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;
    const startedAt = performance.now();
    updateDoc(doc(db, 'patients', patientId), {
      // Clear the strength paragraph and its paired title (empty, not deleted)
      [`${sectionPath}.outputs.${code}`]: "",
      [`${sectionPath}.outputs.${titleCode}`]: "",
      // A deleted paragraph must not remain approved
      [`${sectionPath}.approvedParagraphs.${code}`]: deleteField(),
      // Record the deletion in this paragraph's edit history
      [`${sectionPath}.editHistory.${code}.${timestampKey}`]: {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue: "",
        isDeleted: true,
      },
      // Review metadata (mirrors the approval write)
      [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
      [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
      [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
    }).then(() => {
      // Confirms the deletion persisted in Firestore. Code-only, no PII.
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(`[delete] deleted strength "${code}" in ${elapsedMs}ms`);
      // Analytics mirror of the deletion that just persisted (code only).
      emitParagraphDeleted({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
      });
    }).catch(error => {
      console.error("Error deleting strength paragraph:", error);
      // Roll back ALL optimistic updates and surface the failure
      REPORT_DATA_OUTPUTS[code] = previousValue;
      REPORT_DATA_OUTPUTS[titleCode] = previousTitleValue;
      setDeletedCodes(prev => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      if (wasReviewed) addReviewedCode(code);
      toast.error(
        'The strength paragraph could not be deleted. Please try again.',
        { toastId: 'strength-delete-error' }
      );
    });

    setSectionDirty(true);
  };

  const handleMarkAsDone = async () => {
    if (!state.selectedCode) {
      console.log('No code selected, cannot mark as done');
      return;
    }

    console.log('Starting handleMarkAsDone for code:', state.selectedCode);
    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        console.log('Calling handleParagraphApproval...');
        // Track the in-flight write so Save & Exit awaits it before finalising.
        trackApproval(
          handleParagraphApproval(state.selectedCode, true).catch(error => {
            console.error('Error in handleParagraphApproval:', error);
            // Roll back the optimistic update and tell the clinician — a
            // silently failed approval is exactly how incomplete sections
            // used to slip through to the Patient Report.
            removeReviewedCode(state.selectedCode);
            toast.error(
              'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
              { toastId: 'paragraph-approval-save-error' }
            );
          })
        );

        // Find next paragraph before updating UI state
        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        // Update reviewed state
        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          // Then scroll
          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            // Update state with new paragraph
            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
              clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
              checkedExamples: state.modelInputsFirestore[nextCode]["Checklist Examples Ticked"],
              patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
              informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"],
            }));

            // Pass the new code directly to position update
            setTimeout(() => {
              safeUpdatePosition(nextCode);
            }, 0);
          }, 300);
        } else {
          // This was the last paragraph - unselect it
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
            clinicianDecisions: null,
            checkedExamples: null,
            patientInputs: null,
            informantInputs: null
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  // Update handleSkip with the same scroll logic
  const handleSkip = () => {
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      // Find next paragraph BEFORE updating UI state
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        // Then scroll
        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          // Update state with new paragraph
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
            clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
            checkedExamples: state.modelInputsFirestore[nextCode]["Checklist Examples Ticked"],
            patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
            informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"],
          }));

          // Pass the new code directly to position update
          setTimeout(() => {
            safeUpdatePosition(nextCode);
          }, 0);
        }, 300);
      } else {
        // This was the last paragraph - unselect it
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
          checkedExamples: null,
          patientInputs: null,
          informantInputs: null
        }));
      }
    }
  };

  // Add progress calculation
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // Gate the entire interactive UI behind the feature flag. Without this,
  // the legacy doc structure is used during loading, which produces zero
  // matching paragraphs for v2 patients. That causes totalParagraphs === 0,
  // the header shows "Complete Review", and clicking it marks the report as
  // approved despite no paragraphs actually being reviewed.
  if (isLoadingFeatureFlag) {
    return (
      <Layout activePage="dashboard" scrollable={false}>
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-violet-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-slate-600 font-medium">Loading document...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* // Add overlay component when editing */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{
              pointerEvents: 'none' // Allow clicks to pass through to underlying elements
            }}
          />
        )}

       {/* Fixed header section */}
       <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />

          {/* Compare with Claude – dev/staging UAT only */}
          {isCompareAvailable && (
            <CompareModeToolbar
              isLoading={isCompareLoading}
              hasComparison={hasComparison}
              showClaude={showClaude}
              sideBySide={sideBySide}
              progress={compareProgress}
              onGenerate={triggerComparison}
              onShowGemini={() => setViewMode('gemini')}
              onShowClaude={() => setViewMode('claude')}
              onToggleSideBySide={toggleSideBySide}
            />
          )}

          {ssiOverrideActive && (
            <div className="px-4 mb-3">
              <SSIOverrideBanner
                isVisible
                originalClinicianName={
                  ssiLeadOverride?.originalClinicianName ||
                  patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
                  'the original SSI clinician'
                }
                snapshotPath={snapshotPath}
                isCurrentUserLead={isLeadClinician(
                  patientDataModel,
                  auth.currentUser?.uid
                )}
              />
            </div>
          )}
          {!canEdit && (
            <div className="px-4 mb-3">
              <div className="bg-gray-50 rounded-2xl p-4 text-left border border-gray-200">
                <p className="text-sm text-gray-700">
                  You are viewing this page in read-only mode. Only{' '}
                  <strong>{effectiveSSI.clinicianName || 'the reviewing clinician'}</strong>{' '}
                  can edit and approve Semi-Structured Interview paragraphs.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            {/* Main flex container for document and side panel */}
            <div className="flex h-full">
              {/* Left side - Document container */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      subtitle={SECTION_SUBTITLES[title]}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel — hidden in read-only mode */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                  canDelete={!isEditing && isStrengthParagraph(state.selectedCode)}
                  onDelete={handleDeleteStrength}
                />
              </div>

              {sideBySide && hasComparison ? (
                <CompareClaudePanel
                  docStructure={docStructure}
                  claudeOutputs={claudeOutputs}
                  reportOutputs={REPORT_DATA_OUTPUTS}
                  sectionSubtitles={SECTION_SUBTITLES}
                />
              ) : (
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Your checked examples", state.checkedExamples)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation dialog for deleting a strength paragraph */}
      <AlertDialog.Root open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) setPendingDeleteCode(null); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[420px] max-w-[90vw] z-50 shadow-xl">
            <AlertDialog.Title className="text-lg font-bold mb-2">
              {remainingStrengthCount <= 1 ? 'Delete Last Strength Paragraph' : 'Delete Strength Paragraph'}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-5">
              {remainingStrengthCount <= 1
                ? 'This is the last remaining strength paragraph. Deleting it will remove the section about ADHD Strengths from the Patient Report.'
                : 'This strength paragraph will not appear in the Patient Report.'}
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={executeDeleteStrength}
                  className="py-2 px-4 font-semibold rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors"
                >
                  Delete
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Layout>
  );
}